/**
 * Oracle Telegram Watcher API client. All endpoints are owned by numeo-oracle
 * (encapsulated — does not call loads-ingest-service). Mounted at
 * `/v1/telegram-dedicated` on numeo-oracle.
 */
import { oracleServiceAxios } from 'setup/SetupAxios'

export type OracleTgAccountStatus = 'active' | 'inactive' | 'logged_out'

export interface OracleTgAccount {
    phone: string
    status: OracleTgAccountStatus
    createdAt: string
    running?: boolean
}

export interface OracleTgGroup {
    peerId: string
    originalName: string | null
    brokerCompanyName: string | null
    joinLink: string | null
    status: 'active' | 'archived'
    createdAt: string
    updatedAt: string
    watchingAccountsCount?: number
}

export interface ResolvedPeer {
    peerId: string
    title?: string | null
    username?: string | null
    isChannel?: boolean
    isGroup?: boolean
    isUser?: boolean
}

export interface OracleTgBackfillProgress {
    status: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled'
    phone: string
    peerId: string
    started_at: string | null
    finished_at: string | null
    processed: number
    ingested: number
    duplicates: number
    media_transcribed: number
    media_failed: number
    last_message_at: string | null
    error: string | null
    cancel_requested: boolean
    options: { limit: number; since: string | null; transcribeMedia: boolean } | null
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export async function oracleTgSendCode(phone: string): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/accounts/sendCode', { phone })
}

export async function oracleTgSignIn(phone: string, code: string, password?: string): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/accounts/signIn', { phone, code, password })
}

export async function oracleTgLogout(phone: string): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/accounts/logout', { phone })
}

export async function oracleTgListAccounts(): Promise<OracleTgAccount[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: OracleTgAccount[] }>('/telegram-dedicated/accounts')
    return data.data ?? []
}

// ─── Watcher control ─────────────────────────────────────────────────────────

export async function oracleTgStartWatcher(phone: string): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/watcher/start', { phone })
}

export async function oracleTgStopWatcher(phone: string): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/watcher/stop', { phone })
}

// ─── Groups ──────────────────────────────────────────────────────────────────

async function oracleTgListGroups(): Promise<OracleTgGroup[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: OracleTgGroup[] }>('/telegram-dedicated/groups')
    return data.data ?? []
}

export async function oracleTgUpdateGroup(
    peerId: string,
    updates: { originalName?: string | null; brokerCompanyName?: string | null; joinLink?: string | null }
): Promise<OracleTgGroup> {
    const { data } = await oracleServiceAxios.patch<{ success: boolean; data: OracleTgGroup }>(
        `/telegram-dedicated/groups/${encodeURIComponent(peerId)}`,
        updates
    )
    return data.data
}

async function oracleTgDeleteGroup(peerId: string): Promise<void> {
    await oracleServiceAxios.delete(`/telegram-dedicated/groups/${encodeURIComponent(peerId)}`)
}

// ─── Account ↔ group watches ────────────────────────────────────────────────

export async function oracleTgListGroupsForAccount(phone: string): Promise<OracleTgGroup[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: OracleTgGroup[] }>(
        `/telegram-dedicated/accounts/${encodeURIComponent(phone)}/groups`
    )
    return data.data ?? []
}

export async function oracleTgListCustomerGroupsForAccount(phone: string): Promise<OracleTgGroup[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: OracleTgGroup[] }>(
        `/telegram-customer/accounts/${encodeURIComponent(phone)}/groups`
    )
    return data.data ?? []
}

export async function oracleTgWatchGroup(phone: string, peerId: string, opts?: { title?: string; brokerCompanyName?: string }): Promise<void> {
    await oracleServiceAxios.post(`/telegram-dedicated/accounts/${encodeURIComponent(phone)}/watches`, { peerId, ...opts })
}

export async function oracleTgWatchCustomerGroup(phone: string, peerId: string, opts?: { title?: string }): Promise<void> {
    await oracleServiceAxios.post(`/telegram-customer/accounts/${encodeURIComponent(phone)}/watches`, { peerId, ...opts })
}

export async function oracleTgUnwatchGroup(phone: string, peerId: string): Promise<void> {
    await oracleServiceAxios.delete(`/telegram-dedicated/accounts/${encodeURIComponent(phone)}/watches/${encodeURIComponent(peerId)}`)
}

export async function oracleTgUnwatchCustomerGroup(phone: string, peerId: string): Promise<void> {
    await oracleServiceAxios.delete(`/telegram-customer/accounts/${encodeURIComponent(phone)}/watches/${encodeURIComponent(peerId)}`)
}

export async function oracleTgUpdateCustomerGroup(peerId: string, updates: { originalName?: string | null; brokerCompanyName?: string | null }): Promise<void> {
    await oracleServiceAxios.patch(`/telegram-customer/groups/${encodeURIComponent(peerId)}`, updates)
}

export async function oracleTgListDialogs(phone: string, limit = 30): Promise<ResolvedPeer[]> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: ResolvedPeer[] }>(
        `/telegram-dedicated/accounts/${encodeURIComponent(phone)}/dialogs`,
        {
            params: { limit },
        }
    )
    return data.data ?? []
}

async function oracleTgResolvePeer(phone: string, input: string): Promise<ResolvedPeer> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: ResolvedPeer }>(
        `/telegram-dedicated/accounts/${encodeURIComponent(phone)}/resolve`,
        {
            input,
        }
    )
    return data.data
}

export async function oracleTgJoinChat(phone: string, input: string): Promise<ResolvedPeer> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: ResolvedPeer }>(`/telegram-dedicated/accounts/${encodeURIComponent(phone)}/join`, {
        input,
    })
    return data.data
}

// ─── Backfill ────────────────────────────────────────────────────────────────

export async function oracleTgGetBackfillStatus(phone: string, peerId: string): Promise<OracleTgBackfillProgress> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: OracleTgBackfillProgress }>('/telegram-dedicated/backfill/status', {
        params: { phone, peerId },
    })
    return data.data
}

export async function oracleTgStartBackfill(opts: { phone: string; peerId: string; limit?: number; since?: string; transcribeMedia?: boolean }): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/backfill', opts)
}

export async function oracleTgCancelBackfill(phone: string, peerId: string): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/backfill/cancel', { phone, peerId })
}

export async function oracleTgGetCustomerBackfillStatus(phone: string, peerId: string): Promise<OracleTgBackfillProgress> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: OracleTgBackfillProgress }>('/telegram-customer/backfill/status', {
        params: { phone, peerId },
    })
    return data.data
}

export async function oracleTgStartCustomerBackfill(opts: {
    phone: string
    peerId: string
    limit?: number
    since?: string
    transcribeMedia?: boolean
}): Promise<void> {
    await oracleServiceAxios.post('/telegram-customer/backfill', opts)
}

export async function oracleTgCancelCustomerBackfill(phone: string, peerId: string): Promise<void> {
    await oracleServiceAxios.post('/telegram-customer/backfill/cancel', { phone, peerId })
}

// ─── Prepare media ───────────────────────────────────────────────────────────

export type OracleTgMediaPrepareStatus = 'idle' | 'running' | 'completed' | 'failed' | 'cancelled'
export type OracleTgMediaPrepareStep = 'connecting' | 'resolving' | 'fetching' | 'downloading' | 'transcribing' | 'writing' | 'done'

export interface OracleTgMediaPrepareProgress {
    status: OracleTgMediaPrepareStatus
    chat_id: string | null
    started_at: string | null
    finished_at: string | null
    scanned: number
    transcribed: number
    failed: number
    skipped: number
    total: number
    error: string | null
    current_step: OracleTgMediaPrepareStep | null
    current_chat_id: string | null
    current_message_id: string | null
    current_media_type: string | null
    current_bytes: number | null
    current_started_at: string | null
}

export async function oracleTgGetMediaPrepareStatus(): Promise<OracleTgMediaPrepareProgress> {
    const { data } = await oracleServiceAxios.get<{ success: boolean; data: OracleTgMediaPrepareProgress }>('/telegram-dedicated/media/prepare/status')
    return data.data
}

export async function oracleTgStartMediaPrepare(opts?: { chatId?: string }): Promise<{ total: number }> {
    const { data } = await oracleServiceAxios.post<{ success: boolean; data: { total: number } }>('/telegram-dedicated/media/prepare', opts || {})
    return data.data
}

export async function oracleTgCancelMediaPrepare(): Promise<void> {
    await oracleServiceAxios.post('/telegram-dedicated/media/prepare/cancel', {})
}
